import React from 'react';
import { IonContent, IonPage, withIonLifeCycle } from '@ionic/react';
import mapboxgl, { GeoJSONFeature, LayerSpecification, LngLatBounds } from 'mapbox-gl';
import { debounce } from 'lodash';

//	L O C A L E

import { locale___SbDiscoverMapPage } from './sb-discover-map.locale';

//	S T Y L E

import 'mapbox-gl/dist/mapbox-gl.css';
import './sb-discover-map.scss';

//	T Y P E S

import { type___api___response } from '../../types/types.api-response';
import { type___iframe___configParams } from '../../types/types.iframe';
import { type___geo___lngLat, type___map___element___type } from '../../types/types.types';

//	S T A T E   -   P R O P S

import { props___SbDiscoverMapPage, state___SbDiscoverMapPage, state___SbDiscoverMapPage___defaults } from './sb-discover-map.state';

//	L I B S

import { lib_icons } from '../../libs/lib.icons';
import { lib_names } from '../../libs/lib.names';

//	F U N C S

import { funcs_mapbox } from '../../funcs/funcs.mapbox';

//	S E R V I C E S

import { service_GeolocationService } from '../../services/service-geolocation';
import { service_LocalizationService } from '../../services/service-localization';
import { service_RestApiService } from '../../services/service-api';
import { service_Router } from '../../services/service-router';

//	C O M P O N E N T S

import ComCopyrightComponent from '../../components/com-copyright/com-copyright';
import ComDiscoverElementComponent from '../../components/com-discover-element/com-discover-element';
import ComHeaderLogoComponent from '../../components/com-header-logo/com-header-logo';
import ComOverlayComponent from '../../components/com-overlay/com-overlay';

//	C L A S S

class SbDiscoverMapPage extends React.Component<props___SbDiscoverMapPage, state___SbDiscoverMapPage> {

//#region 																							D E C L A R A T I O N S

	private _ABORT___CONTROLLER: AbortController | null = null;
	private _ABORT___SIGNAL: AbortSignal | null = null;

	private readonly _LOCALE: service_LocalizationService = new service_LocalizationService();
	private readonly _API: service_RestApiService = new service_RestApiService();
	private readonly _GEOLOCATION: service_GeolocationService = new service_GeolocationService();
	private readonly _ROUTER: service_Router = new service_Router();

	private readonly funcs___mapbox: funcs_mapbox = new funcs_mapbox();

	private me___geolocation: type___geo___lngLat = [0, 0];
	private me___marker: mapboxgl.Marker;

	private readonly mapbox___map___container: React.RefObject<HTMLDivElement> = React.createRef();

	private mapbox___map___isLoading: boolean = true;
	private mapbox___map___isLoading___icons: boolean = true;

	private mapbox___map: mapboxgl.Map;
	private mapbox___map___center___lng: number = 0;
	private mapbox___map___center___lat: number = 0;
	private mapbox___map___onClickElement: boolean = false;
	private mapbox___map___onMoveEnd: Promise<any>[] = [];

	private readonly mapbox___map___layer_constructor: GeoJSON.FeatureCollection<GeoJSON.Geometry> = { 'type': 'FeatureCollection', 'features': [] };
	private mapbox___map___collection_markers: GeoJSON.FeatureCollection<GeoJSON.Geometry> = this.mapbox___map___layer_constructor;
	private mapbox___map___collection_markers___ids: string[] = [];

	private readonly mapbox___map___marker_collection___type: LayerSpecification['type'] = 'symbol';
	private readonly mapbox___map___marker_collection___source: string = 'source-markers';
	private readonly mapbox___map___marker_collection___source_id: string = 'source-markers-layer';
	private readonly mapbox___map___marker_collection___layout: any = { 'icon-image': [ 'case', ['!=', ['get', 'marker_icon_custom'], 'none'], ['get', 'marker_icon_custom'], ['get', 'marker_icon']], 'icon-size': [ 'interpolate', ['linear'], ['zoom'], 7.5, 0.000, 8, 0.0375, 12, 0.055, 18, 0.11 ]};
	private readonly mapbox___map___marker_collection___filter: any = [ 'any',
		['all', ['==', ['get', 'marker_type'], 'events'], ['==', ['literal', true /* this.state.map___filters['show_events'] */], true]],
		['all', ['==', ['get', 'marker_type'], 'events'], ['==', ['get', 'marker_has_poi'], 'null'], ['==', ['literal', true  /* this.state.map___filters['show_poi'] */], false]],
		['all', ['==', ['get', 'marker_type'], 'poi'], ['==', ['literal', true /* this.state.map___filters['show_poi'] */], true]],
	];

//#endregion

//#region 																							C O N S T R U C T O R

	constructor(
		public props: props___SbDiscoverMapPage,
	) {

		super(props);
		this.state = state___SbDiscoverMapPage___defaults;
		
		this.constructor___debounce();
		
		this.mapbox___map___container = React.createRef();

	}

	private constructor___debounce() : void {
		this.map___load___datas___input = this.map___load___datas___input.bind(this);
		this.map___load___datas = debounce(this.map___load___datas.bind(this), lib_names.lodash.debounce_delay);
	}

//#endregion

//#region 																							M A P

	private map___get_center = async () => {
		if (this.state.map___filters['config_iframe'] && this.state.map___filters['config_iframe']['map_center']) {
			this.mapbox___map___center___lng = this.state.map___filters['config_iframe']['map_center'][0] ?? 9;
			this.mapbox___map___center___lat = this.state.map___filters['config_iframe']['map_center'][1] ?? 39;
			this.me___geolocation = this.state.map___filters['config_iframe']['map_center'];
		} else {
			const ___map_center___coords: type___geo___lngLat | null = await this._GEOLOCATION.geolocation___locate();
			this.mapbox___map___center___lng = ___map_center___coords[0] ?? 9;
			this.mapbox___map___center___lat = ___map_center___coords[1] ?? 39;
			this.me___geolocation = ___map_center___coords;
		}
		return;
	}

	private map___get_center___by_features = (___features: any[]) => {
		if (this.state.map___isCentered === false ) {
	
			let ___coords___divider: number = 0;
			let ___coords___lng___sum: number = 0;
			let ___coords___lat___sum: number = 0;

			for (let ___f___i = 0; ___f___i < ___features.length; ___f___i++) {
				const ___f = ___features[___f___i];
				___coords___lng___sum += ___f['geometry']['coordinates'][0];
				___coords___lat___sum += ___f['geometry']['coordinates'][1];
				___coords___divider++;
			}

			const ___coords___lng___avg: number = ___coords___lng___sum / ___coords___divider;
			const ___coords___lat___avg: number = ___coords___lat___sum / ___coords___divider;

			console.log('___features', ___features)
			console.log('___coords___lng___avg', ___coords___lng___avg)
			console.log('___coords___lat___avg', ___coords___lat___avg)

			this.map___fly___to(___coords___lng___avg, ___coords___lat___avg);

			this.setState({
				map___isCentered: true,
			});

		}
	}

	/*

	private map___get___watch_position = () => {
		if (this.state.map___filters['config_iframe'] && this.state.map___filters['config_iframe']['display_user_position']) {
			navigator.geolocation.watchPosition(
				(___p: GeolocationPosition) => {
					const ___watch_coords___lng: number = ___p.coords.longitude;
					const ___watch_coords___lat: number = ___p.coords.latitude;
					this.me___geolocation = [___watch_coords___lng, ___watch_coords___lat];
					this.me___marker.setLngLat([this.me___geolocation[0], this.me___geolocation[1]]);
				},
				(___e: any) => {
					console.log('Error Following Position   -->   ', ___e);
				}
			);
		}
		return;
	}

	*/

	private map___prepare = async () => {
		await new Promise<void>((resolve) => {
			if (this.mapbox___map___isLoading === true) {

				this.mapbox___map = new mapboxgl.Map({
					accessToken: lib_names.MapBoxGL.accessToken,
					container: 'sb-discover-map---map', //	this.mapbox___map___container.current as HTMLElement,
					style: lib_names.MapBoxGL.style_normal,
					center: [this.mapbox___map___center___lng, this.mapbox___map___center___lat],
					zoom: 1,
					minZoom: 1,
					maxZoom: lib_names.MapBoxGL.maxZoom,
					dragRotate: lib_names.MapBoxGL.dragRotate,
					doubleClickZoom: lib_names.MapBoxGL.doubleClickZoom,
					touchPitch: lib_names.MapBoxGL.touchPitch,
					maxPitch: lib_names.MapBoxGL.maxPitch,
				});

				this.mapbox___map.resize();
				this.mapbox___map___isLoading = false;

				this.mapbox___map.on('load', () => {

				//	this.map___get___watch_position();

					this.mapbox___map___isLoading = false;

					if (this.mapbox___map___isLoading___icons === true) {
						lib_icons.events.map((___i_e: string) => this.funcs___mapbox.map___load___icon(this.mapbox___map, ___i_e, 'events'));
						lib_icons.poi.map((___i_p: string) => this.funcs___mapbox.map___load___icon(this.mapbox___map, ___i_p, 'poi'));
						this.mapbox___map___isLoading___icons = false;
					}

					if (this.state.map___filters['config_iframe'] && this.state.map___filters['config_iframe']['display_user_position']) {
						const ___me___marker___circle: HTMLDivElement = document.createElement('div');
						___me___marker___circle.className = 'sb-discover-map---me---marker';
						const ___me___marker___circle___popup___html: string = '<h3 class="sb-discover-map---me---popup">' + this._LOCALE.translate(locale___SbDiscoverMapPage.your_position) + '</h3>';
						const ___me___marker___circle___popup: mapboxgl.Popup = new mapboxgl.Popup({ offset: 25 }).setHTML(___me___marker___circle___popup___html);
						this.me___marker = new mapboxgl.Marker(___me___marker___circle).setLngLat([this.me___geolocation[0], this.me___geolocation[1]]).addTo(this.mapbox___map);
						this.me___marker.setPopup(___me___marker___circle___popup);
					}

					this.mapbox___map.resize();
					this.map___fly___to(this.mapbox___map___center___lng, this.mapbox___map___center___lat);

					(async () => {
						await this.map___load___datas();
						return;
					})();

			//		setTimeout(() => {
			//			this.mapbox___map.setMinZoom(lib_names.mapboxgl.minZoom);
			//		}, 2500);

				});

				this.mapbox___map.on('preclick', () => {
					this.setState({
						map___element_pane: 'navigation',
					});
				});

				this.mapbox___map.on('moveend', async () => {
					const ___mapbox___map___center: mapboxgl.LngLat = this.mapbox___map.getCenter();
					this.mapbox___map___center___lng = ___mapbox___map___center.lng;
					this.mapbox___map___center___lat = ___mapbox___map___center.lat;
					await this.map___load___datas();
				});

			}
			resolve();
		});
	}

	private map___fly___to = async (___lng: number, ___lat: number, ___zoom: number = (this.state.map___filters['config_iframe']['map_zoom']) ? this.state.map___filters['config_iframe']['map_zoom'] : 18) => {
		this.mapbox___map___center___lng = ___lng;
		this.mapbox___map___center___lat = ___lat;
		this.mapbox___map.flyTo({ center: [ this.mapbox___map___center___lng, this.mapbox___map___center___lat], zoom: ___zoom });
		await this.map___load___datas();
	}

	private map___load___datas___input = async () => {
		await this.map___load___datas();
	}

	private map___load___datas = async () => {
		this.setState({ map___isLoading: true });
		await this.map___read_props___geoJSON();
	}

	private map___addSource___marker_collection = () => {
		this.mapbox___map.addSource(this.mapbox___map___marker_collection___source, {
			type: 'geojson',
			data: this.mapbox___map___collection_markers
		});
		this.mapbox___map.addLayer({
			'minzoom': lib_names.MapBoxGL.minZoomToDiscover,
			'id': this.mapbox___map___marker_collection___source_id,
			'type': this.mapbox___map___marker_collection___type,
			'source': this.mapbox___map___marker_collection___source,
			'layout': this.mapbox___map___marker_collection___layout,
			'filter': this.mapbox___map___marker_collection___filter,
		});
	}

	private map___read_props___geoJSON = async () => {
		
		const ___map___currentBounds: LngLatBounds = this.mapbox___map?.getBounds();
		const ___map___currentBounds___ne: type___geo___lngLat = [ ___map___currentBounds['_ne']['lng'], ___map___currentBounds['_ne']['lat'] ];
		const ___map___currentBounds___sw: type___geo___lngLat = [ ___map___currentBounds['_sw']['lng'], ___map___currentBounds['_sw']['lat'] ];
		const ___map___marker_collection: type___api___response = await this._API.map___read___area(___map___currentBounds___ne, ___map___currentBounds___sw, this.mapbox___map___collection_markers___ids, this.state.map___filters);

		if (___map___marker_collection['response'] !== 'error') {

			this.funcs___mapbox.map___load___icon___customs(this.mapbox___map, this.mapbox___map___collection_markers['features']);

			this.mapbox___map___collection_markers['features'] = [...this.mapbox___map___collection_markers['features'], ...___map___marker_collection['data']['features']];
			this.mapbox___map___collection_markers___ids = this.mapbox___map___collection_markers['features'].map((___f: any) => { return ___f['properties']['marker_id']; })
			
			if (this.mapbox___map.getSource(this.mapbox___map___marker_collection___source)) {
				(this.mapbox___map.getSource(this.mapbox___map___marker_collection___source) as mapboxgl.GeoJSONSource)
					.setData(this.mapbox___map___collection_markers);
			} else { this.map___addSource___marker_collection(); }
			
			this.mapbox___map.setLayoutProperty(this.mapbox___map___marker_collection___source_id, 'visibility', 'visible');
			this.handle___map___onClickElement();
		
			if (this.mapbox___map___collection_markers && this.mapbox___map___collection_markers['features'] && this.mapbox___map___collection_markers['features'].length > 0) {

				if (
					(this.state.map___filters['config_iframe']['target'] === 'events') ||
					(this.state.map___filters['config_iframe']['target'] === 'poi')
				) { this.map___get_center___by_features(this.mapbox___map___collection_markers['features']); }
	
				if (
					(this.state.map___filters['config_iframe']['target'] === 'events' && this.state.map___filters['config_iframe']['params'] && this.state.map___filters['config_iframe']['params'].length === 1) || 
					(this.state.map___filters['config_iframe']['target'] === 'poi' && this.state.map___filters['config_iframe']['params'] && this.state.map___filters['config_iframe']['params'].length === 1)
				) {
					const ___f___properties: any = this.mapbox___map___collection_markers['features'][0]['properties'];
					this.handle___select___map_element(___f___properties['marker_type'], ___f___properties['marker_id'], ___f___properties['marker_props']);
				}

			}

		}

		this.setState({
			map___isLoading: false,
		});

	}

//#endregion

//#region 																							H A N D L E R S

	private handle___map___onClickElement = () => {
		if (this.mapbox___map___onClickElement === false) {

			this.mapbox___map___onClickElement = true;
			this.mapbox___map.on('click', (___e: any) => {

				const ___map___features: GeoJSONFeature[] = this.mapbox___map.queryRenderedFeatures(___e.point, {
					layers: [this.mapbox___map___marker_collection___source_id],
				});

				if (___map___features.length > 0) {

					const ___map___marker: GeoJSONFeature = ___map___features[0];

					const ___map___marker___type: type___map___element___type = ___map___marker['properties']['marker_type'] as type___map___element___type;
					const ___map___marker___id: string = ___map___marker['properties']['marker_id'] as string;
					const ___map___marker___props: any = ___map___marker['properties']['marker_props'] ?? null;
					const ___map___marker___props___parsed: any = (___map___marker___props) ? JSON.parse(___map___marker___props) : null;		//	workaround

					this.setState({
						map___element_pane___content_type: ___map___marker___type,
						map___element_pane___content_id: ___map___marker___id,
						map___element_pane___content_props: ___map___marker___props___parsed,
					}, () => {
						this.handle___select___map_element(___map___marker___type, ___map___marker___id, ___map___marker___props___parsed);
					});
	
				}
			});
		}
	}

	private handle___select___map_element = (___type: type___map___element___type, ___id: string, ___props: any | null = null) => {
		this.setState({
			map___element_pane: 'element',
			map___element_pane___content_type: ___type,
			map___element_pane___content_id: ___id,
			map___element_pane___content_props: ___props,
		});
	}

//#endregion

//#region 																							L I F E C Y C L E

	ionViewDidEnter() : void {

		this._ABORT___CONTROLLER = new AbortController();
		this._ABORT___SIGNAL = this._ABORT___CONTROLLER.signal;
		
		const ___router_params: type___iframe___configParams | null = this._ROUTER.params___parse(this.props);

		this.setState({ 
			...state___SbDiscoverMapPage___defaults,
			map___filters: {...state___SbDiscoverMapPage___defaults['map___filters'], config_iframe: ___router_params ?? null }
		}, async () => {
			await this.map___get_center().then(async () => {
				await this.map___prepare();
			});
		});

	}

	ionViewWillLeave() : void {
		if (this._ABORT___CONTROLLER) { this._ABORT___CONTROLLER.abort(); }
		if (this.mapbox___map) { this.mapbox___map.remove(); }
	}

//#endregion

//#region 																							R E N D E R

	render() : React.ReactNode {
		return <>
			<IonPage>
				<IonContent forceOverscroll={ false } scrollX={ false } scrollY={ false }>
					<div className="sb-page---container ignore-limits sb-discover-map---body">

						{/*
							<div className="sb-discover-map---header">
								<ComHeaderLogoComponent logo___theme="light"/>
							</div>
						*/}

						<div className="sb-discover-map---container">
							<div ref={ this.mapbox___map___container } id="sb-discover-map---map" className="sb-discover-map---map" />
						</div>

						{(() => { switch (this.state.map___element_pane) {
							case 'element': return <>
								<ComDiscoverElementComponent element___type={ this.state.map___element_pane___content_type } element___id={ this.state.map___element_pane___content_id } element___props={ this.state.map___element_pane___content_props }/>
							</>; break;
						}})()}

						{/*
							<ComCopyrightComponent />
						*/}

					</div>
					<ComOverlayComponent />
				</IonContent>
			</IonPage>
		</>;
	}

//#endregion

}

//	E X P O R T

export default withIonLifeCycle(SbDiscoverMapPage);